import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Toast from '../../components/toast/Toast';
import { ButtonSize } from '../../enums/common';
import { validateWebhook } from './utils';
import { texts } from './consts';
import Loader from '../../components/loader/Loader';
import AnimatedInput from '../../components/AnimatedInput/AnimatedInput';
import {
  ActionButton,
  DescriptionText,
  DescriptionTitle,
  FormAction,
  FormTableHorizontalRows,
} from '../../components/onlineForm/onlineForm';
import Title from '../../components/onlineTitle/Title';
import { Environments, EnvironmentStatus, IWebhook } from '../../models/developerPortal';

const SActionsPanel = styled.div(
  ({ theme }) => `
    display: flex;
    max-width: 300px;
    min-width: 150px;
    align-self: end;
    & button {
      margin: 30px 0 0 0;
    }
    
    & button:not(:last-child) {
      margin-right: 10px;
    }
`
);

const SFormTable = styled(FormTableHorizontalRows)`
  display: flex;
  flex-direction: column;
  max-width: 740px;
  margin-bottom: 20px;

  .th {
    width: 160px;
  }

  .td.signature {
    display: flex;
    overflow: hidden;
  }

  .th.webhook {
    height: 70px;
  }

  .td.input-field {
    .input-container {
      width: 100%;
    }
  }

  .td.webhook {
    height: 70px;

    input,
    .error-message {
      font-size: ${({ theme }) => theme.font.size.smallMedium};
    }
  }

  .webhook-secret {
    font-size: ${({ theme }) => theme.font.size.smallMedium};
    overflow: hidden;
    display: inline-block;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 148px;
  }

  &.prod-keys {
    background-color: #faf8f6;
  }

  .test-button {
    color: #3156ff;
  }
`;

export interface IWebhooksBoxProps extends IWebhooksBaseProps {
  isProdWebhooks?: boolean;
}

const WebhooksBox: React.FC<IWebhooksBoxProps> = ({
  isProdWebhooks,
  webhooks,
  updateWebhook,
  testWebhookUrl,
  setWebhook,
}) => {
  const [error, setError] = useState(null);

  const webhook = (webhooks?.[0] || {}) as IWebhook;
  const { url, key: webhookSecret, environment } = webhook;

  const [disabled, setDisabled] = React.useState(false);

  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newURL = e.target.value;
      const succeed = validateWebhook(newURL);

      setError(!succeed ? texts.WEBHOOK_ERROR : null);
      setWebhook({ ...webhook, url: newURL });
    },
    [webhook]
  );

  const configureWebhook = useCallback(async () => {
    setDisabled(true);
    await updateWebhook(environment);
    setDisabled(false);
  }, [environment, updateWebhook]);

  const _testWebhookUrl = useCallback(() => testWebhookUrl(url), [testWebhookUrl, url]);

  const copy = useCallback(() => navigator.clipboard.writeText(webhookSecret), [webhookSecret]);

  useEffect(() => {
    setDisabled(!url?.length || error);
  }, [webhook]);

  return (
    <div>
      <DescriptionTitle>{`${isProdWebhooks ? 'Production' : 'Sandbox'} Webhooks:`}</DescriptionTitle>
      <SFormTable className={isProdWebhooks ? 'prod-keys' : ''}>
        <div className="tr">
          <div className="th vertical webhook">
            <p>Webhooks URL</p>
          </div>
          <div className="td input-field webhook">
            <AnimatedInput
              {...{
                className: 'input-container',
                animatedLabel: '',
                value: url,
                onChange: handleOnChange,
                error,
              }}
            />
          </div>
        </div>
        <div className="tr">
          <div className="th vertical">
            <p>Sunbit Signature</p>
          </div>
          <div className="td signature">
            <p className="webhook-secret">{webhookSecret}</p>
            {webhookSecret && (
              <Toast text="Text was copied to clipboard" callback={copy}>
                <FormAction>Copy</FormAction>
              </Toast>
            )}
          </div>
        </div>
        <SActionsPanel>
          {url && (
            <ActionButton
              {...{
                className: 'test-button',
                dataAt: 'sign-in-submit',
                buttonText: 'Test',
                submit: _testWebhookUrl,
                disabled,
                size: ButtonSize.small,
              }}
              secondary
            />
          )}
          <ActionButton
            {...{
              dataAt: 'sign-in-submit',
              buttonText: 'Configure',
              submit: configureWebhook,
              disabled,
              size: ButtonSize.small,
            }}
          />
        </SActionsPanel>
      </SFormTable>
    </div>
  );
};

export interface IWebhooksBaseProps {
  updateWebhook: (environment: Environments) => Promise<void>;
  testWebhookUrl: (webhookUrl: string) => Promise<void>;
  webhooks: IWebhook[];
  prodRequestStatus?: EnvironmentStatus;
  setWebhook: (webhook: IWebhook) => void;
}

export interface IWebhooksProps extends IWebhooksBaseProps {
  waiting: boolean;
}

const Webhooks: React.FC<IWebhooksProps> = ({
  webhooks,
  updateWebhook,
  testWebhookUrl,
  prodRequestStatus,
  setWebhook,
  waiting,
}) => {
  // Separating envs keys
  const reduceInitials: { sandboxWebhooks: Array<IWebhook>; prodWebhooks: Array<IWebhook> } = {
    sandboxWebhooks: [],
    prodWebhooks: [],
  };
  const { sandboxWebhooks, prodWebhooks } = useMemo(
    () =>
      webhooks.reduce((acc, k) => {
        if (k.environment === Environments.prod) acc.prodWebhooks.push(k);
        else acc.sandboxWebhooks.push(k);

        return acc;
      }, reduceInitials),
    [webhooks]
  );

  const boxProps = {
    updateWebhook,
    testWebhookUrl,
    setWebhook,
  };

  return (
    <>
      {waiting && <Loader active />}

      <DescriptionText>
        <Title alignLeft>Webhooks</Title>
        You can use Sunbit’s webhooks to get notified when an event happens and get real time
        <br />
        insights for your reporting, customer outreach, and remarketing strategies.
      </DescriptionText>

      <WebhooksBox {...{ webhooks: sandboxWebhooks, ...boxProps }} />
      {prodRequestStatus === EnvironmentStatus.ENABLED && (
        <WebhooksBox {...{ webhooks: prodWebhooks, ...boxProps }} isProdWebhooks />
      )}
    </>
  );
};

export default Webhooks;
